import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal, Select } from 'antd';
import apiClient from '../services/api';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { User, Company, Employee } from '../types';
import { useAuth } from '../hooks/useAuth';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import { LockOutlined } from '@ant-design/icons';
import { accentInsensitiveIncludes, normalizeText } from '../utils/accentUtils';

const roleOptions = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'company_admin', label: 'Quản lý công ty' },
  { value: 'manager', label: 'Chuyên viên tài sản' },
  { value: 'employee_user', label: 'Nhân viên' },
];



const UserPage = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [changePwdModal, setChangePwdModal] = useState<{ visible: boolean, user: User | null }>({ visible: false, user: null });
  const [pwdForm] = Form.useForm();
  const [pwdLoading, setPwdLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const fetchUsers = async () => {
    await fetchWithLoading(
      () => apiClient.getUsers(),
      setLoading,
      (data) => setUsers(data as User[]),
      'Không thể tải danh sách người dùng'
    );
  };

  const fetchCompanies = async () => {
    await fetchWithLoading(
      () => apiClient.getCompanies(),
      () => { },
      (data) => setCompanies(data as Company[]),
      'Không thể tải danh sách công ty'
    );
  };

  const fetchEmployees = async () => {
    await fetchWithLoading(
      () => apiClient.getEmployees(),
      () => { },
      (data) => setEmployees(data as Employee[]),
      'Không thể tải danh sách nhân viên'
    );
  };

  const companyMap = useMemo(() => Object.fromEntries(companies.map((c: any) => [c.id, c.name])), [companies]);
  const columns = useMemo(() => [
    { title: 'Tên đăng nhập', dataIndex: 'username', key: 'username' },
    { title: 'Tên hiển thị', dataIndex: 'employee_name', key: 'employee_name', render: (text: string, record: User) => text || record.username },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      render: (role: string, record: User) => {
        if (role === 'employee_user' && record.managed_location_name) {
          return `Kiểm soát tài sản ${record.managed_location_name}`;
        }
        return roleOptions.find(r => r.value === role)?.label || role;
      }
    },
    { title: 'Công ty', dataIndex: 'company_id', key: 'company_id', render: (company_id: number) => companyMap[company_id] || '' },
    {
      title: 'Hành động', key: 'action', width: 170,
      render: (_: any, record: User) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          {(user?.role === 'super_admin' || user?.id === record.id) && (
            <Button icon={<LockOutlined />} size="small" onClick={() => setChangePwdModal({ visible: true, user: record })} />
          )}
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />
        </Space>
      )
    }
  ], [companyMap, users, employees]);

  useEffect(() => { fetchUsers(); fetchCompanies(); fetchEmployees(); }, []);

  if (!user || user.role !== 'super_admin') {
    return null; // hoặc return <div>Bạn không có quyền truy cập trang này.</div>
  }

  const handleSubmit = async (values: any) => {
    try {
      const submitValues = { ...values };
      if (submitValues.company_id) {
        submitValues.company_id = Number(submitValues.company_id);
        if (isNaN(submitValues.company_id)) submitValues.company_id = undefined;
      }

      // Explicitly set employee_id to null if it's undefined (cleared)
      if (submitValues.employee_id === undefined) {
        submitValues.employee_id = null;
      }

      // Nếu update và password rỗng thì xóa field password
      if (editingUser && (!submitValues.password || submitValues.password.trim() === '')) {
        delete submitValues.password;
      }
      if (editingUser) {
        await apiClient.updateUser(editingUser.id, submitValues);
        message.success('Cập nhật người dùng thành công!');
      } else {
        await apiClient.createUser(submitValues);
        message.success('Tạo người dùng thành công!');
      }
      form.resetFields();
      setEditingUser(null);
      setModalVisible(false);
      fetchUsers();
      fetchEmployees();
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editingUser ? 'Cập nhật thất bại!' : 'Tạo người dùng thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    const linkedEmp = employees.find(e => e.user_id === record.id);
    form.setFieldsValue({
      username: record.username,
      role: record.role,
      company_id: record.company_id,
      password: '',
      employee_id: linkedEmp ? linkedEmp.id : undefined
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteUser(id);
      message.success('Đã xóa người dùng');
      fetchUsers();
    } catch {
      message.error('Xóa người dùng thất bại!');
    }
  };

  const handleDeleteClick = (record: User) => {
    confirmDelete('Bạn có chắc muốn xóa người dùng này?', () => handleDelete(record.id));
  };

  const handleChangePwd = async () => {
    try {
      setPwdLoading(true);
      const values = await pwdForm.validateFields();
      await apiClient.changeUserPassword(
        changePwdModal.user!.id,
        values.oldPassword,
        values.newPassword
      );
      message.success('Đổi mật khẩu thành công!');
      setChangePwdModal({ visible: false, user: null });
      pwdForm.resetFields();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Đổi mật khẩu thất bại!';
      message.error(msg);
    } finally {
      setPwdLoading(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    accentInsensitiveIncludes(u.username, searchValue) ||
    accentInsensitiveIncludes(u.employee_name || '', searchValue)
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm tên đăng nhập"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => { setEditingUser(null); form.resetFields(); setModalVisible(true); }}>Thêm mới</Button>
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        title={editingUser ? 'Cập nhật người dùng' : 'Tạo mới người dùng'}
        onCancel={() => { setModalVisible(false); setEditingUser(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="username" label="Tên đăng nhập" rules={[{ required: true, message: 'Nhập tên đăng nhập!' }]}>
            <Input className="ams-input" placeholder="Tên đăng nhập" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }} />
          </Form.Item>
          <Form.Item name="password" label="Mật khẩu" rules={[{ required: !editingUser, message: 'Nhập mật khẩu!' }, { min: 8, message: 'Mật khẩu tối thiểu 8 ký tự!' }]}>
            <Input.Password className="ams-input" placeholder={editingUser ? "Để trống nếu không đổi mật khẩu" : "Nhập mật khẩu"} size="large" style={{ width: '100%', borderRadius: 8, height: 40 }} />
          </Form.Item>
          <Form.Item name="role" label="Vai trò" rules={[{ required: true, message: 'Chọn vai trò!' }]}>
            <Select options={roleOptions} placeholder="Chọn vai trò" size="large" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="company_id" label="Công ty">
            <Select
              allowClear
              showSearch
              placeholder="Chọn công ty (nếu có)"
              filterOption={(input, option) =>
                accentInsensitiveIncludes(option?.label?.toString() || '', input)
              }
              size="large"
              style={{ width: '100%' }}
              options={companies.map(c => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.company_id !== currentValues.company_id}
          >
            {({ getFieldValue }) => {
              const selectedCompanyId = getFieldValue('company_id');
              const filteredOptions = employees
                .filter(emp => !emp.user_id || emp.user_id === editingUser?.id)
                .filter(emp => !selectedCompanyId || emp.company_id === selectedCompanyId)
                .map(emp => ({
                  value: emp.id,
                  label: `${emp.full_name} (${emp.employee_code || 'N/A'})`
                }));

              return (
                <Form.Item name="employee_id" label="Nhân viên liên kết">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Chọn nhân viên (nếu có)"
                    filterOption={(input, option) =>
                      accentInsensitiveIncludes(option?.label?.toString() || '', input)
                    }
                    size="large"
                    style={{ width: '100%' }}
                    options={filteredOptions}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button className="ams-btn" type="primary" htmlType="submit" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }}>{editingUser ? 'Cập nhật' : 'Tạo mới'}</Button>
          </Form.Item>
          {editingUser && (
            <Form.Item style={{ marginBottom: 0 }}>
              <Button className="ams-btn" style={{ width: '100%', borderRadius: 8, height: 40 }} size="large" onClick={() => { setEditingUser(null); form.resetFields(); setModalVisible(false); }}>Hủy</Button>
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal
        open={changePwdModal.visible}
        title={`Đổi mật khẩu cho ${changePwdModal.user?.username}`}
        onCancel={() => { setChangePwdModal({ visible: false, user: null }); pwdForm.resetFields(); }}
        footer={null}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePwd}>
          {user?.role !== 'super_admin' && (
            <Form.Item name="oldPassword" label="Mật khẩu cũ" rules={[{ required: true, message: 'Nhập mật khẩu cũ!' }]}>
              <Input.Password placeholder="Mật khẩu cũ" />
            </Form.Item>
          )}
          <Form.Item name="newPassword" label="Mật khẩu mới" rules={[{ required: true, message: 'Nhập mật khẩu mới!' }, { min: 8, message: 'Mật khẩu tối thiểu 8 ký tự!' }]}>
            <Input.Password placeholder="Mật khẩu mới" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Xác nhận mật khẩu mới" dependencies={["newPassword"]} rules={[
            { required: true, message: 'Xác nhận mật khẩu mới!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
              },
            }),
          ]}>
            <Input.Password placeholder="Xác nhận mật khẩu mới" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button className="ams-btn" type="primary" htmlType="submit" size="large" loading={pwdLoading} style={{ width: '100%', borderRadius: 8, height: 40 }}>Đổi mật khẩu</Button>
          </Form.Item>
        </Form>
      </Modal>
      <Table
        dataSource={filteredUsers}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default UserPage; 